// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Button, Form, Message, Icon, Segment, Dropdown, TextArea, Label } from 'semantic-ui-react';
import { emailTemplateApi, EmailTemplateCreateDTO } from '../api/emailTemplateApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import { providerApi, Provider } from '../api/providerApi';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';

function EmailTemplateFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<EmailTemplateCreateDTO>({
    templateType: 'ADVISOR_LOA_PROGRESS',
    templateLevel: 'BROKER',
    subjectTemplate: '',
    bodyTemplateHtml: '',
    bodyTemplateText: '',
    isActive: true
  });

  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadReferenceData();
    if (isEditMode && id) {
      loadTemplate(id);
    }
  }, [id, isEditMode]);

  const loadReferenceData = async () => {
    try {
      const [brokeragesData, providersData, policyTypesData] = await Promise.all([
        brokerageApi.getAll(),
        providerApi.getAll(),
        policyTypeApi.getAll()
      ]);
      setBrokerages(brokeragesData.content);
      setProviders(providersData.content);
      setPolicyTypes(policyTypesData.content);
    } catch (err) {
      console.error('Failed to load reference data:', err);
      setError('Failed to load reference data');
    }
  };

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const data = await emailTemplateApi.getById(templateId);
      setFormData({
        templateType: data.templateType,
        templateLevel: data.templateLevel,
        brokerageId: data.brokerageId,
        providerId: data.providerId,
        policyTypeId: data.policyTypeId,
        subjectTemplate: data.subjectTemplate,
        bodyTemplateHtml: data.bodyTemplateHtml,
        bodyTemplateText: data.bodyTemplateText || '',
        isActive: data.isActive
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateTypeChange = (value: string) => {
    setFormData({
      ...formData,
      templateType: value as 'ADVISOR_LOA_PROGRESS' | 'ADVISOR_LOA_COMPLETED' | 'PROVIDER_LOA_NOTIFICATION',
      // Reset to valid level for template type
      templateLevel: (value === 'ADVISOR_LOA_PROGRESS' || value === 'ADVISOR_LOA_COMPLETED')
        ? 'BROKER'
        : formData.templateLevel
    });
  };

  const handleTemplateLevelChange = (value: string) => {
    // Clear foreign keys when changing level
    setFormData({
      ...formData,
      templateLevel: value as 'DEFAULT' | 'BROKER' | 'PROVIDER' | 'POLICY' | 'BROKER_PROVIDER' | 'BROKER_POLICY',
      brokerageId: undefined,
      providerId: undefined,
      policyTypeId: undefined
    });
  };

  // Extracted validation logic to reduce cognitive complexity
  const validateTemplateLevel = (): string | null => {
    const level = formData.templateLevel;
    const { brokerageId, providerId, policyTypeId } = formData;

    const validationRules: Record<string, { condition: boolean; message: string }> = {
      BROKER_POLICY: {
        condition: !brokerageId || !policyTypeId,
        message: 'BROKER_POLICY level requires both Brokerage and Policy Type'
      },
      BROKER_PROVIDER: {
        condition: !brokerageId || !providerId,
        message: 'BROKER_PROVIDER level requires both Brokerage and Provider'
      },
      BROKER: {
        condition: !brokerageId,
        message: 'BROKER level requires Brokerage'
      },
      PROVIDER: {
        condition: !providerId,
        message: 'PROVIDER level requires Provider'
      },
      POLICY: {
        condition: !policyTypeId,
        message: 'POLICY level requires Policy Type'
      }
    };

    const rule = validationRules[level];
    if (rule?.condition) {
      return rule.message;
    }
    return null;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Validate required fields based on template level
      const validationError = validateTemplateLevel();
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      if (isEditMode && id) {
        await emailTemplateApi.update(id, formData);
      } else {
        await emailTemplateApi.create(formData);
      }

      setSuccess(true);
      setTimeout(() => navigate('/email-templates'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const templateTypeOptions = [
    { key: 'ADVISOR_LOA_PROGRESS', text: 'Advisor Progress Notification', value: 'ADVISOR_LOA_PROGRESS' },
    { key: 'ADVISOR_LOA_COMPLETED', text: 'Advisor Completion Notification', value: 'ADVISOR_LOA_COMPLETED' },
    { key: 'PROVIDER_LOA_NOTIFICATION', text: 'Provider LOA Notification', value: 'PROVIDER_LOA_NOTIFICATION' }
  ];

  // Advisor templates only support DEFAULT and BROKER levels
  const isAdvisorTemplate = formData.templateType === 'ADVISOR_LOA_PROGRESS' ||
                            formData.templateType === 'ADVISOR_LOA_COMPLETED';

  const templateLevelOptions = isAdvisorTemplate ? [
    { key: 'DEFAULT', text: 'DEFAULT - System-wide default', value: 'DEFAULT' },
    { key: 'BROKER', text: 'BROKER - Brokerage-specific', value: 'BROKER' }
  ] : [
    { key: 'DEFAULT', text: 'DEFAULT - System-wide default', value: 'DEFAULT' },
    { key: 'BROKER', text: 'BROKER - Brokerage-specific', value: 'BROKER' },
    { key: 'PROVIDER', text: 'PROVIDER - Provider-specific', value: 'PROVIDER' },
    { key: 'POLICY', text: 'POLICY - Policy type-specific', value: 'POLICY' },
    { key: 'BROKER_PROVIDER', text: 'BROKER_PROVIDER - Brokerage + Provider', value: 'BROKER_PROVIDER' },
    { key: 'BROKER_POLICY', text: 'BROKER_POLICY - Brokerage + Policy Type (highest priority)', value: 'BROKER_POLICY' }
  ];

  const brokerageOptions = brokerages.map(b => ({ key: b.id, text: b.name, value: b.id }));
  const providerOptions = providers.map(p => ({ key: p.id, text: p.name, value: p.id }));
  const policyTypeOptions = policyTypes.map(pt => ({ key: pt.id, text: pt.name, value: pt.id }));

  // Determine which fields to show based on template level
  const showBrokerage = ['BROKER', 'BROKER_PROVIDER', 'BROKER_POLICY'].includes(formData.templateLevel);
  const showProvider = ['PROVIDER', 'BROKER_PROVIDER'].includes(formData.templateLevel);
  const showPolicyType = ['POLICY', 'BROKER_POLICY'].includes(formData.templateLevel);

  const availableVariables = [
    { name: 'clientName', description: 'Client full name' },
    { name: 'profileName', description: 'Profile name' },
    { name: 'advisorName', description: 'Advisor full name' },
    { name: 'brokerageName', description: 'Brokerage name' },
    { name: 'providerName', description: 'Insurance provider name' },
    { name: 'policyNumber', description: 'Policy number' },
    { name: 'policyType', description: 'Policy type name' },
    { name: 'signedCount', description: 'Number of signatures collected' },
    { name: 'totalSignatories', description: 'Total number of required signatories' },
    { name: 'signingUrl', description: 'Client signing URL' },
    { name: 'completedDate', description: 'LOA completion date' }
  ];

  return (
    <Container style={{ marginTop: '50px' }}>
      <Button basic onClick={() => navigate('/email-templates')} style={{ marginBottom: '20px' }}>
        <Icon name='arrow left' /> Back to Email Templates
      </Button>

      <Header as='h1'>
        <Icon name='mail' />
        <Header.Content>
          {isEditMode ? 'Edit Email Template' : 'Create Email Template'}
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>Email template {isEditMode ? 'updated' : 'created'} successfully!</p>
        </Message>
      )}

      <Segment>
        <Form loading={loading}>
          {/* Template Type */}
          <Form.Field required>
            <label htmlFor="template-type">Template Type</label>
            <Dropdown
              id="template-type"
              fluid
              selection
              options={templateTypeOptions}
              value={formData.templateType}
              onChange={(_e, data) => handleTemplateTypeChange(data.value as string)}
              placeholder='Select template type'
            />
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
              {formData.templateType === 'ADVISOR_LOA_PROGRESS' &&
                'Sent to advisor after each individual signature (no attachment)'}
              {formData.templateType === 'ADVISOR_LOA_COMPLETED' &&
                'Sent to advisor when all signatures are collected (with signed LOA PDF)'}
              {formData.templateType === 'PROVIDER_LOA_NOTIFICATION' &&
                'Sent to providers when LOA is fully signed (with signed LOA PDF)'}
            </div>
          </Form.Field>

          {/* Template Level */}
          <Form.Field required>
            <label htmlFor="template-level">Template Level</label>
            <Dropdown
              id="template-level"
              fluid
              selection
              options={templateLevelOptions}
              value={formData.templateLevel}
              onChange={(_e, data) => handleTemplateLevelChange(data.value as string)}
              placeholder='Select template level'
            />
            <Message info size='tiny'>
              <Message.Header>Template Hierarchy</Message.Header>
              <p>
                Templates are resolved from highest to lowest priority.
                {isAdvisorTemplate ? (
                  <> Advisor templates support: <strong>BROKER</strong> → <strong>DEFAULT</strong></>
                ) : (
                  <> Provider templates support: <strong>BROKER_POLICY</strong> → <strong>BROKER_PROVIDER</strong> → <strong>POLICY</strong> → <strong>PROVIDER</strong> → <strong>BROKER</strong> → <strong>DEFAULT</strong></>
                )}
              </p>
            </Message>
          </Form.Field>

          {/* Conditional Foreign Key Fields */}
          {showBrokerage && (
            <Form.Field required>
              <label htmlFor="brokerage">Brokerage</label>
              <Dropdown
                id="brokerage"
                fluid
                selection
                search
                options={brokerageOptions}
                value={formData.brokerageId}
                onChange={(_e, data) => setFormData({ ...formData, brokerageId: data.value as string })}
                placeholder='Select brokerage'
              />
            </Form.Field>
          )}

          {showProvider && (
            <Form.Field required>
              <label htmlFor="provider">Provider</label>
              <Dropdown
                id="provider"
                fluid
                selection
                search
                options={providerOptions}
                value={formData.providerId}
                onChange={(_e, data) => setFormData({ ...formData, providerId: data.value as string })}
                placeholder='Select provider'
              />
            </Form.Field>
          )}

          {showPolicyType && (
            <Form.Field required>
              <label htmlFor="policy-type">Policy Type</label>
              <Dropdown
                id="policy-type"
                fluid
                selection
                search
                options={policyTypeOptions}
                value={formData.policyTypeId}
                onChange={(_e, data) => setFormData({ ...formData, policyTypeId: data.value as string })}
                placeholder='Select policy type'
              />
            </Form.Field>
          )}

          {/* Subject Template */}
          <Form.Field required>
            <label htmlFor="subject-template">Subject Template</label>
            <Form.Input
              id="subject-template"
              placeholder='e.g., LOA Progress: {{signedCount}} of {{totalSignatories}} signatures - {{profileName}}'
              value={formData.subjectTemplate}
              onChange={(e) => setFormData({ ...formData, subjectTemplate: e.target.value })}
            />
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
              Use <code>{'{{variableName}}'}</code> for variable substitution
            </div>
          </Form.Field>

          {/* HTML Body Template */}
          <Form.Field required>
            <label htmlFor="body-template-html">HTML Body Template</label>
            <TextArea
              id="body-template-html"
              rows={12}
              placeholder={`<!DOCTYPE html>\n<html>\n<body>\n  <h1>LOA Progress Update</h1>\n  <p>Dear {{advisorName}},</p>\n  <p>Progress: {{signedCount}} of {{totalSignatories}} signatures collected.</p>\n</body>\n</html>`}
              value={formData.bodyTemplateHtml}
              onChange={(e) => setFormData({ ...formData, bodyTemplateHtml: e.target.value })}
              style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
            />
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
              Use <code>{'{{variableName}}'}</code> or <code>{'[[${variableName}]]'}</code> for variable substitution
            </div>
          </Form.Field>

          {/* Plain Text Body Template */}
          <Form.Field>
            <label htmlFor="body-template-text">Plain Text Body Template (Optional)</label>
            <TextArea
              id="body-template-text"
              rows={8}
              placeholder={`LOA Progress Update\n\nDear {{advisorName}},\n\nProgress: {{signedCount}} of {{totalSignatories}} signatures collected.`}
              value={formData.bodyTemplateText}
              onChange={(e) => setFormData({ ...formData, bodyTemplateText: e.target.value })}
              style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
            />
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
              Fallback for email clients that don't support HTML
            </div>
          </Form.Field>

          {/* Available Variables Help */}
          <Segment secondary>
            <Header as='h4'>
              <Icon name='info circle' />
              Available Variables
            </Header>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {availableVariables.map(v => (
                <div key={v.name}>
                  <Label size='tiny' color='teal'>
                    <code>{`{{${v.name}}}`}</code>
                  </Label>
                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '3px' }}>
                    {v.description}
                  </div>
                </div>
              ))}
            </div>
          </Segment>

          {/* Active Status */}
          <Form.Checkbox
            label='Active (template will be used in email resolution hierarchy)'
            checked={formData.isActive}
            onChange={(_e, data) => setFormData({ ...formData, isActive: !!data.checked })}
          />

          {/* Submit Buttons */}
          <Button
            primary
            onClick={handleSubmit}
            disabled={!formData.subjectTemplate || !formData.bodyTemplateHtml || loading}
          >
            <Icon name='save' /> {isEditMode ? 'Update Template' : 'Create Template'}
          </Button>
          <Button onClick={() => navigate('/email-templates')}>Cancel</Button>
        </Form>
      </Segment>
    </Container>
  );
}

export default EmailTemplateFormPage;
